import apiClient from './api-client';

export interface PortfolioSummary {
  total_balance: number;
  loan_count: number;
  average_yield: number;
  weighted_average_life: number;
  groupings: Array<{
    name: string;
    balance: number;
    count: number;
    percentage: number;
  }>;
}

export interface PortfolioRiskMetrics {
  average_ltv: number;
  average_dscr: number;
  watchlist_count: number;
  default_count: number;
  expected_loss: number;
  risk_distribution: Array<{
    name: string | null;
    count: number;
  }>;
}

export interface LoanValuation {
  loan_id: number;
  pricing_scenario: string;
  maturity_assumption: string;
  client_loan_number: string;
  loan_name: string;
  sector: string; // Was property_sector
  property_type: string;
  property_lifecycle_financing: string;
  sponsor_borrower: string;
  original_balance: number;
  current_balance: number;
  currency: string;
  client_percentage: number;
  pik_balance: number;
  position_in_capital_stack: string;
  amortization_type: string;
  periodicity: string;
  interest_day_count: string;
  loan_status: string;
  commentary: string;
  interest_type: string;
  fixed_rate_coupon: number;
  floating_rate_index: string;
  floating_rate_margin: number;
  credit_spread_decimal: number;
  loss_scenario: string | null;
  pd: number;
  ead: number;
  lgd: number;
  lag_to_recovery: number;
  default_date: string | null;
  cdr: number;
  ltv_current: number;
  dscr_current: number;
  debt_yield_current: number | null;
  noi: number | null;
  property_appraisal_value: number | null;
  property_appraisal_value_date: string | null;
  origination_date: string;
  first_payment_date: string;
  original_maturity_date: string;
  prepayment_lockout_end_date: string | null;
  io_end_date: string | null;
  first_extension_date: string | null;
  first_extension_fee: number | null;
  second_extension_date: string | null;
  second_extension_fee: number | null;
  third_extension_date: string | null;
  third_extension_fee: number | null;
  exit_fee: number | null;
  property_location: string;
  in_forbearance: string;
  forbearance_start_date: string | null;
  forbearance_type: string | null;
  valuation_date: string;
  interest_accrual_type: string;
  effective_maturity_date: string;
  remaining_months: number;
  remaining_years: number;
  wal_years: number;
  term_bucket: string;
  benchmark_type: string;
  exact_rate: number;
  prev_rate: number;
  next_rate: number;
  prev_term: number;
  next_term: number;
  benchmark_yield: number;
  matrix_spread_bps: number;
  matched_property_type: string;
  matched_ltv_bucket: string;
  matched_loan_type: string;
  rpx_total_spread_bps: number; // Was effective_spread_bps
  ltv_factor_adjustment_bps: number;
  sasb_premium_bps: number;
  lifecycle_adjustment_bps: number;
  dscr_adjustment_bps: number;
  ltv_performance_adjustment_bps: number;
  loan_status_adjustment_bps: number;
  senior_tiering_adjustment_bps: number;
  mezzanine_adjustment_bps: number;
  rpx_base_spread_bps: number; // Was total_rpx_adjustment_bps
  adjusted_spread_bps: number;
  credit_spread: number;
  component_yield_decimal: number; // Was market_yield_decimal
  component_yield_pct: number; // Was market_yield
  price_clean_decimal: number; // Was price
  modified_duration_years: number;
  convexity: number;
  loss_scenario_formatted: string;
  default_scenario: string;
  coupon_description: string;
  contract_rate: number;
  accrual_days: number;
  day_count_divisor: number;
  accrued_interest: number;
  fair_value_clean: number; // Was fair_value
  fair_value_dirty: number; // Was fair_value_with_accrued/fair_value_plus_accrued
  price_dirty_decimal: number; // Was price_with_accrued/price_including_accrued
  unrealized_gain_loss: number;
  price_plus_25bps: number;
  price_minus_25bps: number;
  lower_price: number;
  upper_price: number;
  lower_fv: number;
  upper_fv: number;
  dv01: number;
  lower_mey: number;
  upper_mey: number;
}

// Consolidated Valuation Reports interface (Updated with new field mappings from backend)
export interface ValuationReportResult {
  loan_id: number;
  loan_name: string;
  current_balance: number;
  fair_value_clean: number;
  price_clean_pct: number;
  component_yield_pct: number;
  modified_duration_years: number;
  wal_years: number;
  valuation_date: string;
  interest_type: string;
}

export interface PortfolioAnalysis {
  total_loans: number;
  total_balance: number;
  total_fair_value: number;
  weighted_avg_price: number;
  weighted_avg_yield: number;
  sector_breakdown: Record<
    string,
    { count: number; balance: number; fair_value: number }
  >;
  status_breakdown: Record<
    string,
    { count: number; balance: number; fair_value: number }
  >;
}

export interface ValuationReportsResponse {
  metadata: {
    source: string;
    total_loans: number;
    analysis_date: string;
    description: string;
    last_materialized_update?: string;
  };
  valuation_results: ValuationReportResult[];
  portfolio_analysis: PortfolioAnalysis;
}

export const portfolioService = {
  // Get portfolio summary
  async getPortfolioSummary(): Promise<PortfolioSummary> {
    const response = await apiClient.get('/api/v1/portfolios/summary');
    return response.data;
  },

  // Get portfolio risk metrics
  async getPortfolioRiskMetrics(): Promise<PortfolioRiskMetrics> {
    const response = await apiClient.get('/api/v1/portfolios/risk-metrics');
    return response.data;
  },

  // Get valuation portfolio summary
  async getValuationPortfolioSummary(): Promise<unknown[]> {
    const response = await apiClient.get('/api/v1/valuation/portfolio-summary');
    return response.data;
  },

  // Get consolidated valuation reports (NEW - replaces all legacy pricing endpoints)
  async getValuationReports(): Promise<ValuationReportResult[]> {
    const response = await apiClient.get('/api/v1/valuation/valuation-reports');
    return response.data;
  },

  // Get loan valuation
  async getLoanValuation(): Promise<LoanValuation[]> {
    const response = await apiClient.get('/api/v1/valuation/loan-valuation');
    return response.data;
  },
};
